/**
 * What to call the things hanging off a controller.
 *
 * The database calls them field_nodes, but the right word depends on the radio.
 * An ESP-NOW mesh genuinely has routers - that is what the firmware calls them
 * and what the operator sees in the ESP-NOW tooling. An XBee site has nodes.
 * Picking one word for both would make the UI wrong for one set of users, so
 * the label follows the controller's transport.
 *
 * Purely cosmetic: router_mac stays router_mac everywhere in code and on the
 * wire, because it appears in firmware payloads.
 */

export type Transport = string | null | undefined;

export interface NodeNoun {
  one: string;
  many: string;
  /** Lowercase, for mid-sentence use. */
  oneLower: string;
  manyLower: string;
}

const XBEE: NodeNoun = { one: 'Node', many: 'Nodes', oneLower: 'node', manyLower: 'nodes' };
const MESH: NodeNoun = { one: 'Router', many: 'Routers', oneLower: 'router', manyLower: 'routers' };

/**
 * @param transport field_controllers.transport - 'xbee' | 'espnow' | 'lora' | 'http'
 *
 * Defaults to the mesh wording when transport is unknown: ESP-NOW is the
 * overwhelming majority of deployed controllers, so an in-flight load or an
 * older row without the column reads correctly rather than briefly flipping.
 */
export function nodeNoun(transport: Transport): NodeNoun {
  return transport === 'xbee' ? XBEE : MESH;
}

/** Convenience for the common "3 nodes" / "1 node" case. */
export function countNodes(n: number, transport: Transport): string {
  const noun = nodeNoun(transport);
  return `${n} ${n === 1 ? noun.oneLower : noun.manyLower}`;
}
